import { useEffect, useState, useMemo } from "react";
import { getImageUrl } from "../firebase/storageService";
import { authFetch } from "../utils/authFetch";

const LOGOS_MODALIDADE = {
  SCERTS: "/inn_yellow.png",
  CCE: "/inn_orange.png",
  PRECOCE: "/inn_green.png",
};

const diasAtras = (data) => {
  if (!data) return Infinity;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(data);
  alvo.setHours(0, 0, 0, 0);
  const diffMs = hoje - alvo;
  return diffMs / (1000 * 60 * 60 * 24);
};

const calcularIdade = (birthdate) => {
  if (!birthdate) return "-";
  const nascimento = new Date(birthdate);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }
  return idade >= 0 ? `${idade} anos` : "-";
};

const obterLogoModalidade = (nome) => {
  if (!nome) return null;
  const upper = nome.toUpperCase();
  if (upper.includes("SCERT")) return LOGOS_MODALIDADE.SCERTS;
  if (upper.includes("CCE")) return LOGOS_MODALIDADE.CCE;
  if (upper.includes("PRECOCE")) return LOGOS_MODALIDADE.PRECOCE;
  return null;
};

const NovosPacientesCard = () => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const buscarPacientes = async () => {
      try {
        setLoading(true);
        const response = await authFetch("patient?enabled=true");
        if (!response.ok) throw new Error("Erro ao buscar pacientes");
        const data = await response.json();
        if (cancelado) return;

        const novos = data
          .filter((paciente) => {
            const ehNovoPorData = diasAtras(paciente.interventionStartDate) <= 30;
            const ehEmAvaliacao = paciente.statusId === 1;
            // Novo paciente = (começou intervenção nos últimos 30 dias) OU (status Em Avaliação)
            return ehNovoPorData || ehEmAvaliacao;
          })
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", "pt-BR", {
              sensitivity: "base",
            })
          );

        // Primeiro exibe os dados (sem depender das imagens) e já tira o loading.
        // Marca fotoCarregada=false para mostrar loader sutil até terminar o fetch das imagens.
        setPacientes(
          novos.map((p) => ({
            ...p,
            fotoCarregada: false,
          }))
        );
        setLoading(false);

        // Depois carrega as fotos em background e atualiza a lista
        const enriquecidos = await Promise.all(
          novos.map(async (paciente) => {
            if (cancelado) return paciente;
            try {
              const foto = await getImageUrl("patient", paciente.id);
              return { ...paciente, foto: foto || null, fotoCarregada: true };
            } catch {
              // Se der erro ou não encontrar imagem, apenas marca como carregado,
              // assim o loader é removido e mostra o avatar padrão.
              return { ...paciente, foto: null, fotoCarregada: true };
            }
          })
        );

        if (!cancelado) {
          setPacientes((prev) => {
            // Garante que mantemos mesma ordem original
            return prev.map((p) => {
              const atualizado = enriquecidos.find((e) => e.id === p.id);
              return atualizado || p;
            });
          });
        }
      } catch (err) {
        console.error("Erro ao carregar novos pacientes", err);
      } finally {
        // não mexe mais no loading aqui; já foi desligado após carregar os dados
      }
    };

    buscarPacientes();

    return () => {
      cancelado = true;
    };
  }, []);

  const listaRenderizada = useMemo(() => {
    if (loading) return [];
    return pacientes;
  }, [loading, pacientes]);

  return (
    <div className="col-12 mb-4">
      <div className="card card-dashboard-20" style={{ border: '1px solid #dee2e6', borderRadius: '12px' }}>
        <div className="card-header pb-0">
          <div className="row">
            <div className="col-lg-6 col-12">
              <h6 className="mb-2 font-weight-bolder" style={{ marginBottom: '0.75rem' }}>Novos Pacientes</h6>
            </div>
          </div>
        </div>
        <div className="card-body px-0 pb-2">
          <div className="table-responsive">
            <table className="table align-items-center mb-0">
              <thead>
                <tr>
                  <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Paciente
                  </th>
                  <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Idade
                  </th>
                  <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Pais
                  </th>
                  <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Modalidade
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && listaRenderizada.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-sm">
                      Nenhum novo paciente encontrado
                    </td>
                  </tr>
                )}
                {listaRenderizada.map((paciente) => {
                  const pais = [
                    paciente.fatherName,
                    paciente.motherName,
                  ]
                    .filter(Boolean)
                    .join(" / ");
                  const logo = obterLogoModalidade(
                    paciente.modality?.name || paciente.modalityName
                  );
                  return (
                    <tr key={paciente.id}>
                      <td className="text-xs mb-0">
                        <div className="d-flex px-2 py-1">
                          <div className="position-relative me-3">
                            <div
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '1.75em',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: !paciente.foto ? 'rgba(0,0,0,0.03)' : 'transparent',
                              }}
                            >
                              {paciente.fotoCarregada ? (
                                <img
                                  src={
                                    paciente.foto || require("../assets/img/perfilvazio.png")
                                  }
                                  alt={`Foto ${paciente.name}`}
                                  className="avatar avatar-sm"
                                  style={{ borderRadius: "1.75em" }}
                                />
                              ) : (
                                <div
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                  style={{ opacity: 0.5 }}
                                >
                                  <span className="visually-hidden">Carregando foto...</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex flex-column justify-content-center">
                            <p className="mb-0 text-xs" style={{ fontSize: '1.08rem' }}>{paciente.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-xs mb-0" style={{ fontSize: '1.08rem' }}>{calcularIdade(paciente.birthdate)}</td>
                      <td className="text-xs mb-0" style={{ fontSize: '1.08rem' }}>{pais || "-"}</td>
                      <td className="text-xs mb-0 text-center">
                        {logo ? (
                          <img
                            src={logo}
                            alt="Logo modalidade"
                            style={{
                              maxHeight: "36px",
                              width: "auto",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <span className="text-muted text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NovosPacientesCard;


